import React from "react";
import "./NotificationTypeIcon.scss";

/**
 * Icon at the start of every row in the header notifications dropdown: a type-specific
 * Iconsax "Linear" glyph (24px grid, 1.5px stroke) inside a tinted circle.
 *
 * The same file lives in tiryaq-doctor-pharmacy-app, tiryaq-lab-app, tiryaq-imaging-app,
 * tiryaq-nurse-app, tiryaq-physiotherapy-app and tiryaq-insurance-app
 * (src/components/notifications/), together with NotificationTypeIcon.scss. Keep the copies
 * identical.
 *
 * Icon vocabulary:
 *   bell      orange  incoming order / booking request, and anything not matched below
 *                     (call ended, general alerts)
 *   medicine  blue    order status updated (accepted, approved, confirmed, in progress, started)
 *             orange  order ready / dispatched / on the way
 *             green   order completed / delivered / collected
 *             red     order cancelled / rejected / expired
 *   calendar  blue    appointment added / confirmed / rescheduled
 *             red     leave added, appointment cancelled / declined
 *   edit      orange  login credentials / password / profile updated
 *   shield    green   document verification succeeded (red: rejected, orange: pending)
 *   card      green   payment received / collected / credited (red: failed or refunded, blue: invoice)
 *   message   blue    chat message
 *   document  green   report / results shared (blue: operation request)
 *             green   insurance claim approved (red: rejected, blue: other claim updates)
 *   timer     orange  SLA / confirmation reminder (red: breached or expired)
 */

// Small pencil shared by the calendar-edit and medicine-bottle glyphs (from Iconsax "calendar-edit").
const PENCIL =
  "M19.21 15.77l-3.54 3.54c-.14.14-.27.4-.3.59l-.19 1.35c-.07.49.27.83.76.76l1.35-.19c.19-.03.46-.16.59-.3l3.54-3.54c.61-.61.9-1.32 0-2.22-.89-.89-1.6-.6-2.21.01Z";
const PENCIL_EDGE = "M18.7 16.28c.3 1.08 1.14 1.92 2.22 2.22";

const GLYPHS = {
  // Iconsax "notification"
  bell: (
    <>
      <path d="M12.02 2.91c-3.31 0-6 2.69-6 6v2.89c0 .61-.26 1.54-.57 2.06L4.3 15.77c-.71 1.18-.22 2.49 1.08 2.93 4.31 1.44 8.96 1.44 13.27 0 1.21-.4 1.74-1.83 1.08-2.93l-1.15-1.91c-.3-.52-.56-1.45-.56-2.06V8.91c0-3.3-2.7-6-6-6Z" />
      <path d="M13.87 3.2a6.754 6.754 0 0 0-3.7 0c.29-.74 1.01-1.26 1.85-1.26.84 0 1.56.52 1.85 1.26Z" />
      <path
        d="M15.02 19.06c0 1.65-1.35 3-3 3-.82 0-1.58-.34-2.12-.88a3.01 3.01 0 0 1-.88-2.12"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />
    </>
  ),
  // Medicine bottle (cap, bottle with a plus) with the calendar-edit pencil, drawn on the
  // Iconsax grid so it matches the rest of the set.
  medicine: (
    <>
      <path d="M8.25 2h7.5c.69 0 1.25.56 1.25 1.25v1c0 .69-.56 1.25-1.25 1.25h-7.5C7.56 5.5 7 4.94 7 4.25v-1C7 2.56 7.56 2 8.25 2Z" />
      <path d="M12.5 22H8.5C6.5 22 5 20.5 5 18.5V9c0-2 1.5-3.5 3.5-3.5h7C17.5 5.5 19 7 19 9v3.5" />
      <path d="M12 11v5M9.5 13.5h5" />
      <path d={PENCIL} />
      <path d={PENCIL_EDGE} />
    </>
  ),
  // Iconsax "calendar-edit"
  calendar: (
    <>
      <path d="M8 2v3M16 2v3M3.5 9.09h17" />
      <path d={PENCIL} />
      <path d={PENCIL_EDGE} />
      <path d="M12 22H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5V12" />
      <path d="M11.995 13.7h.01M8.294 13.7h.01M8.294 16.7h.01" strokeWidth="2" />
    </>
  ),
  // Iconsax "edit"
  edit: (
    <>
      <path d="M11 2H9C4 2 2 4 2 9v6c0 5 2 7 7 7h6c5 0 7-2 7-7v-2" />
      <path d="M16.04 3.02 8.16 10.9c-.3.3-.6.89-.66 1.32l-.43 3.01c-.16 1.09.61 1.85 1.7 1.7l3.01-.43c.42-.06 1.01-.36 1.32-.66l7.88-7.88c1.36-1.36 2-2.94 0-4.94-2-2-3.58-1.36-4.94 0Z" />
      <path d="M14.91 4.15a7.144 7.144 0 0 0 4.94 4.94" />
    </>
  ),
  // Iconsax "shield-tick"
  shield: (
    <>
      <path d="M10.49 2.23 5.5 4.11c-1.15.43-2.09 1.79-2.09 3.01v7.43c0 1.18.78 2.73 1.73 3.44l4.3 3.21c1.41 1.06 3.73 1.06 5.14 0l4.3-3.21c.95-.71 1.73-2.26 1.73-3.44V7.12c0-1.23-.94-2.59-2.09-3.02l-4.99-1.87c-.85-.31-2.21-.31-3.04 0Z" />
      <path d="m9.05 11.87 1.61 1.61 4.3-4.3" />
    </>
  ),
  // Iconsax "card"
  card: (
    <>
      <path d="M2 8.505h20M6 16.505h2M10.5 16.505h4" />
      <path d="M6.44 3.505h11.11c3.56 0 4.45.88 4.45 4.39v8.21c0 3.51-.89 4.39-4.44 4.39H6.44c-3.55.01-4.44-.87-4.44-4.38v-8.22c0-3.51.89-4.39 4.44-4.39Z" />
    </>
  ),
  // Iconsax "message"
  message: (
    <>
      <path d="M8.5 19H8c-4 0-6-1-6-6V8c0-4 2-6 6-6h8c4 0 6 2 6 6v5c0 4-2 6-6 6h-.5c-.31 0-.61.15-.8.4l-1.5 2c-.66.88-1.74.88-2.4 0l-1.5-2c-.16-.22-.53-.4-.8-.4Z" />
      <path d="M15.996 11h.01M11.995 11h.01M7.995 11h.008" strokeWidth="2" />
    </>
  ),
  // Iconsax "document-text"
  document: (
    <>
      <path d="M21 7v10c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V7c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z" />
      <path d="M14.5 4.5v2c0 1.1.9 2 2 2h2M8 13h4M8 17h8" />
    </>
  ),
  // Iconsax "timer-1"
  timer: (
    <>
      <path d="M20.75 13.25c0 4.83-3.92 8.75-8.75 8.75s-8.75-3.92-8.75-8.75S7.17 4.5 12 4.5s8.75 3.92 8.75 8.75ZM12 8v5" />
      <path d="M9 2h6" />
    </>
  ),
};

// Delivery channels the notification service stores in place of the semantic type.
const CHANNEL_TYPES = new Set(["", "REALTIME", "PUSH", "EMAIL", "SMS"]);

const parseData = (value) => {
  if (value && typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

// Live socket rows carry the semantic type (e.g. NEW_LAB_BOOKING). Persisted rows from the REST
// list carry the channel ("realtime") instead, sometimes with the semantic type in the payload.
const semanticType = (notification, data) => {
  const candidates = [
    notification.notification_type,
    notification.notificationType,
    data.notification_type,
  ];
  for (const candidate of candidates) {
    const type = String(candidate || "").trim().toUpperCase();
    if (!CHANNEL_TYPES.has(type)) return type;
  }
  return "";
};

const statusTone = (text) => {
  if (/cancel|reject|declin|expired|removed|fail/.test(text)) return "red";
  if (/complet|deliver|collected|finished/.test(text)) return "green";
  if (/ready|dispatch|on the way|out for/.test(text)) return "orange";
  return null;
};

/**
 * Picks the glyph and tint for a notification. Matches the semantic type when the row has one,
 * otherwise the English title (the title column is always English; Arabic lives in titleArabic).
 * Returns `{ icon, tone }`.
 */
export const getNotificationVisual = (notification) => {
  const n = notification || {};
  const data = parseData(n.data);
  const typeText = semanticType(n, data).toLowerCase().replace(/_/g, " ");
  const title = String(n.title || "").trim().toLowerCase();
  const body = String(n.body || "").toLowerCase();
  const key = `${typeText} | ${title || body}`;

  if (/chat message/.test(key) || /^new message/.test(title)) {
    return { icon: "message", tone: "blue" };
  }
  if (/credential|password|login|\bprofile\b/.test(key)) {
    return { icon: "edit", tone: "orange" };
  }
  if (/\bleave\b/.test(key)) {
    return { icon: "calendar", tone: "red" };
  }
  if (/document|verification|verified|licen[cs]e/.test(key)) {
    if (/reject|fail|declin|expired/.test(key)) return { icon: "shield", tone: "red" };
    if (/request|pending|submitted/.test(key)) return { icon: "shield", tone: "orange" };
    return { icon: "shield", tone: "green" };
  }
  if (/operation request/.test(key)) {
    return { icon: "document", tone: "blue" };
  }
  if (/report|result/.test(key)) {
    return { icon: "document", tone: "green" };
  }
  if (/\bsla\b|reminder|expir|after hours|before it is cancelled/.test(key)) {
    return {
      icon: "timer",
      tone: /breached|expired|sla breach\b/.test(key) ? "red" : "orange",
    };
  }
  if (/payment|paid|\bcod\b|settlement|income|invoice|refund|\bfee\b|payin|wallet|earning/.test(key)) {
    if (/fail|reject|cancel|refund/.test(key)) return { icon: "card", tone: "red" };
    if (/invoice|request/.test(key)) return { icon: "card", tone: "blue" };
    return { icon: "card", tone: "green" };
  }
  if (/appointment|reschedul|home visit|revisit|consultation|schedul/.test(key)) {
    return {
      icon: "calendar",
      tone: /cancel|declin|reject|missed|expired/.test(key) ? "red" : "blue",
    };
  }
  const isSplitDecision = /separate|split|decision/.test(key);
  if (
    !isSplitDecision &&
    (/^new\b/.test(typeText) ||
      /^new\b/.test(title) ||
      /incoming|request|order created|booking created|test order/.test(key))
  ) {
    return { icon: "bell", tone: "orange" };
  }
  // Insurance claim updates (insurance portal). New claims are caught by the bell rule above.
  if (/\bclaim/.test(key)) {
    if (/reject|declin|cancel|fail/.test(key)) return { icon: "document", tone: "red" };
    if (/approv|accept|settled|complet/.test(key)) return { icon: "document", tone: "green" };
    return { icon: "document", tone: "blue" };
  }
  if (
    isSplitDecision ||
    /order|booking|status|test|sample|visit|session|procedure|technician|nurse|therapist|dispatch|assign|accept|approv|reject|cancel|complet|deliver|confirm|progress|ready|assessment|prescription|item|removed|updat/.test(
      key,
    )
  ) {
    // Generic "status updated / changed" titles name the new status only in the body.
    const tone =
      statusTone(key) || (/status/.test(key) ? statusTone(body) : null) || "blue";
    return { icon: "medicine", tone };
  }
  // The bell is always orange, matching the design.
  return { icon: "bell", tone: "orange" };
};

/**
 * `visual` optionally overrides the automatic choice (e.g. `{ icon: "document", tone: "green" }`)
 * for rows a panel recognises from app-specific payload fields.
 */
const NotificationTypeIcon = ({ notification, visual }) => {
  const { icon, tone } = visual || getNotificationVisual(notification);
  return (
    <div
      className={`dl-notification-type-icon dl-notification-type-icon--${tone}`}
      aria-hidden="true"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        focusable="false"
      >
        {GLYPHS[icon] || GLYPHS.bell}
      </svg>
    </div>
  );
};

export default NotificationTypeIcon;
